import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/upr-api";
import { ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import NotifyMeButton from "@/components/NotifyMeButton";
import { useCart } from "@/contexts/CartContext";
import DOMPurify from "dompurify";
import { RICH_TEXT_SANITIZE } from "@/lib/rich-text";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "vinyl", label: "Vinyl" },
  { key: "cd", label: "CDs" },
  { key: "tshirt", label: "T-Shirts" },
  { key: "cap", label: "Caps" },
  { key: "other", label: "Other" },
];

export default function Merch() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedVariant, setSelectedVariant] = useState({}); // {merchId: "M"}
  const [qty, setQty] = useState({}); // {merchId: 1}
  const [promoBanner, setPromoBanner] = useState("");
  const { add: addToCart } = useCart();

  useEffect(() => {
    api.get("/merch").then(({ data }) => setItems(data)).finally(() => setLoading(false));
    api.get("/shipping/rates")
      .then(({ data }) => setPromoBanner((data.promo_banner || "").trim()))
      .catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const base = filter === "all" ? items : items.filter((i) => i.category === filter);
    // Sort ascending by display_order (lower = first). Ties broken by newest.
    return [...base].sort((a, b) => {
      const ao = Number(a.display_order ?? 100);
      const bo = Number(b.display_order ?? 100);
      if (ao !== bo) return ao - bo;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
  }, [items, filter]);

  const onBuy = (item) => {
    const q = Math.max(1, Math.min(10, Number(qty[item.id]) || 1));
    if ((item.variants || []).length > 0) {
      const label = selectedVariant[item.id];
      if (!label) { toast.error("Please pick a size first."); return; }
      const v = item.variants.find((x) => x.label === label);
      if (!v || (v.stock || 0) <= 0) { toast.error("That size is sold out."); return; }
      if (q > (v.stock || 0)) { toast.error(`Only ${v.stock} left in size ${label}.`); return; }
      addToCart(item, label, q);
    } else {
      addToCart(item, "", q);
    }
    toast.success(`Added ${q} × ${item.name} to cart`);
    setQty((s) => ({ ...s, [item.id]: 1 })); // reset qty back to 1
  };

  const bumpQty = (id, delta) => {
    setQty((s) => ({ ...s, [id]: Math.max(1, Math.min(10, (Number(s[id]) || 1) + delta)) }));
  };

  const variantInStock = (item) => {
    if ((item.variants || []).length === 0) {
      if (typeof item.stock === "number") return item.stock > 0;
      return !!item.in_stock;
    }
    return item.variants.some((v) => (v.stock || 0) > 0);
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-20" data-testid="merch-page">
      {promoBanner && (
        <div
          className="mb-8 px-4 py-3 font-heading uppercase tracking-widest text-xs md:text-sm text-center"
          style={{
            background: "var(--upr-primary)",
            color: "var(--upr-bg)",
            letterSpacing: "0.14em",
          }}
          data-testid="merch-promo-banner"
        >
          {promoBanner}
        </div>
      )}
      <div className="upr-eyebrow mb-6"><span className="upr-divider"/>Store</div>
      <h1 className="font-heading font-black tracking-tighter leading-[0.88] text-5xl md:text-7xl lg:text-8xl" style={{ letterSpacing: "-0.035em" }}>
        Merch & vinyl.
      </h1>
      <p className="mt-5 max-w-xl text-base md:text-lg" style={{ color: "var(--upr-muted)" }}>
        Physical editions, label apparel and goods. Limited runs, screen-printed and pressed with care.
      </p>

      <div className="mt-10 flex flex-wrap gap-2" data-testid="merch-filters">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            type="button"
            data-testid={`merch-filter-${c.key}`}
            onClick={() => setFilter(c.key)}
            className={
              "font-heading uppercase tracking-widest text-xs px-4 py-2 border transition-colors " +
              (filter === c.key
                ? "bg-[color:var(--upr-fg)] text-[color:var(--upr-bg)] border-[color:var(--upr-fg)]"
                : "bg-transparent text-[color:var(--upr-fg)] border-[color:var(--upr-border)] hover:border-[color:var(--upr-fg)]")
            }
          >
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => <div key={i} className="aspect-square animate-pulse" style={{ background: "var(--upr-surface)" }} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-12 text-sm" style={{ color: "var(--upr-muted)" }}>Nothing in this category yet.</div>
      ) : (
        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8" data-testid="merch-grid">
          {filtered.map((m) => {
            const hasVariants = (m.variants || []).length > 0;
            const inStock = variantInStock(m);
            const pickedVariant = selectedVariant[m.id];
            return (
            <article key={m.id} className="upr-card-hover" data-testid={`merch-card-${m.id}`}>
              <div className="upr-cover aspect-square relative" style={{ border: "1px solid var(--upr-border)" }}>
                <img src={m.image_url} alt={m.name} className="w-full h-full object-cover" />
                {!inStock && (
                  <div className="absolute top-2 left-2 px-2 py-1 text-[0.62rem] font-heading uppercase tracking-widest" style={{ background: "var(--upr-fg)", color: "var(--upr-bg)" }}>
                    Sold out
                  </div>
                )}
                {m.release_id && (
                  <div className="absolute top-2 right-2 px-2 py-1 text-[0.62rem] font-heading uppercase tracking-widest" style={{ background: "var(--upr-primary)", color: "var(--upr-bg)" }} data-testid={`wav-badge-${m.id}`}>
                    + MP3
                  </div>
                )}
              </div>
              <div className="pt-3">
                <div className="upr-eyebrow" style={{ fontSize: "0.6rem" }}>{m.category}</div>
                <div className="font-heading font-extrabold text-lg leading-tight mt-1" style={{ letterSpacing: "-0.01em" }}>{m.name}</div>
                {m.description && (
                  <div
                    className="text-sm upr-rich-teaser"
                    style={{ color: "var(--upr-muted)" }}
                    data-testid={`merch-description-${m.id}`}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.description, RICH_TEXT_SANITIZE) }}
                  />
                )}

                {/* Price */}
                <div className="mt-4 font-heading font-black text-xl" style={{ letterSpacing: "-0.01em" }}>
                  {m.currency} {Number(m.price).toFixed(2)}
                </div>

                {/* Variant (size) picker — LARGE, clearly labelled, right above the CTA row */}
                {hasVariants && (
                  <div className="mt-4" data-testid={`variants-${m.id}`}>
                    <div className="upr-eyebrow mb-2">
                      Size {pickedVariant && <span style={{ color: "var(--upr-primary)" }}>· {pickedVariant}</span>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {m.variants.map((v) => {
                        const out = (v.stock || 0) <= 0;
                        const active = pickedVariant === v.label;
                        return (
                          <button
                            key={v.label}
                            type="button"
                            disabled={out}
                            onClick={() => setSelectedVariant((s) => ({ ...s, [m.id]: v.label }))}
                            data-testid={`variant-${m.id}-${v.label}`}
                            className="font-heading font-extrabold uppercase tracking-widest text-sm min-w-[44px] h-11 px-3 border transition-all"
                            style={{
                              background: active ? "var(--upr-fg)" : "transparent",
                              color: out ? "var(--upr-muted)" : (active ? "var(--upr-bg)" : "var(--upr-fg)"),
                              borderColor: active ? "var(--upr-fg)" : "var(--upr-border)",
                              opacity: out ? 0.45 : 1,
                              textDecoration: out ? "line-through" : "none",
                              cursor: out ? "not-allowed" : "pointer",
                              transform: active ? "translateY(-1px)" : "none",
                            }}
                          >
                            {v.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Quantity + Add-to-Cart row — grouped, prominent, easy to reach */}
                {inStock ? (
                  <div className="mt-4 flex flex-col sm:flex-row items-stretch gap-2" data-testid={`buy-row-${m.id}`}>
                    <div className="flex items-stretch border self-start sm:self-auto" style={{ borderColor: "var(--upr-border)" }} data-testid={`qty-${m.id}`}>
                      <button
                        type="button"
                        onClick={() => bumpQty(m.id, -1)}
                        disabled={(qty[m.id] || 1) <= 1}
                        aria-label="Decrease quantity"
                        data-testid={`qty-dec-${m.id}`}
                        className="w-10 h-11 flex items-center justify-center font-heading text-lg transition-colors disabled:opacity-30 hover:bg-[color:var(--upr-surface)]"
                      >−</button>
                      <div className="w-10 h-11 flex items-center justify-center font-heading font-extrabold text-base border-x" style={{ borderColor: "var(--upr-border)" }} data-testid={`qty-val-${m.id}`}>
                        {qty[m.id] || 1}
                      </div>
                      <button
                        type="button"
                        onClick={() => bumpQty(m.id, +1)}
                        disabled={(qty[m.id] || 1) >= 10}
                        aria-label="Increase quantity"
                        data-testid={`qty-inc-${m.id}`}
                        className="w-10 h-11 flex items-center justify-center font-heading text-lg transition-colors disabled:opacity-30 hover:bg-[color:var(--upr-surface)]"
                      >+</button>
                    </div>
                    <button
                      type="button"
                      onClick={() => onBuy(m)}
                      disabled={hasVariants && !pickedVariant}
                      data-testid={`buy-${m.id}`}
                      className="upr-btn flex-1 h-11 !py-0 text-xs sm:text-sm !px-3 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ShoppingBag size={14} />
                      <span className="truncate">{hasVariants && !pickedVariant ? "Pick a size" : "Add to cart"}</span>
                    </button>
                  </div>
                ) : (
                  <div className="mt-4">
                    <NotifyMeButton merchId={m.id} testid="notify" />
                  </div>
                )}
                {m.release_id && (
                  <div className="mt-2 text-[0.65rem]" style={{ color: "var(--upr-muted)" }} data-testid={`wav-note-${m.id}`}>
                    Includes high-quality MP3 download after checkout.
                  </div>
                )}
              </div>
            </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
